import { useState } from "react";
import { MEDIA } from "@/data/site";
import { useLang } from "@/i18n/LangContext";
import { smoothScrollTo } from "@/hooks/useSmoothScroll";

export default function Hero() {
  const { t } = useLang();
  // isOpaque = false means the page loads in transparent mode (no frosted overlay)
  const [isOpaque, setIsOpaque] = useState(false);

  return (
    <section
      data-testid="hero-section"
      className="relative w-full min-h-[100vh] overflow-hidden bg-[#F5F5F7]"
    >
      {/* Image stack */}
      <div className="absolute inset-0">
        <img
          src={MEDIA.hero_clear}
          alt="Villa with clear smart glass"
          className="absolute inset-0 w-full h-full object-cover"
          draggable="false"
        />
        <img
          src={MEDIA.hero_frosted}
          alt="Villa with opaque smart glass"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1100ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isOpaque ? "opacity-100" : "opacity-0"
          }`}
          style={{
            // Pixel-precise mullion alignment with the clear image. After
            // matching the source sizes to 1344x768, the renderings still
            // differ by ~1 source-pixel (the frosted is internally ~0.17%
            // wider between the left and right mullions). This tiny scale
            // + translate compensates exactly so the central mullion and
            // right thicker pillar stay locked during the transition.
            transformOrigin: "0 0",
            transform: "translateX(0.09%) scaleX(0.99827)",
          }}
          draggable="false"
        />
        {/* Subtle scrim for legibility */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-white/40" />
      </div>

      {/* Top spacer */}
      <div className="relative z-10 pt-28 md:pt-36" />

      {/* Headline */}
      <div className="relative z-10 mt-10 md:mt-16 max-w-7xl mx-auto px-6 md:px-10">
        <h1
          data-testid="hero-title"
          className="font-display font-light tracking-[-0.04em] leading-[0.95] text-[#1D1D1F] text-[12vw] md:text-[9vw] lg:text-[7.5vw]"
        >
          {t("hero.title_1")}
          <br />
          <span className="font-extralight text-[#1D1D1F]/80">
            {t("hero.title_2")}
          </span>
        </h1>

        <p
          data-testid="hero-subtitle"
          className="mt-8 max-w-xl font-body text-base md:text-lg text-[#1D1D1F]/75 leading-relaxed"
        >
          {t("hero.subtitle")}
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-3">
          <button
            data-testid="hero-cta-products"
            onClick={() =>
              smoothScrollTo(document.getElementById("products"), { offset: -64 })
            }
            className="bg-[#1D1D1F] hover:bg-black text-white text-sm font-body font-medium rounded-full px-7 py-3.5 transition-colors"
          >
            {t("hero.cta_explore")}
          </button>
          <button
            data-testid="hero-cta-contact"
            onClick={() =>
              smoothScrollTo(document.getElementById("contact"), { offset: -64 })
            }
            className="border border-[#1D1D1F]/20 hover:border-[#1D1D1F] bg-white/70 backdrop-blur text-[#1D1D1F] text-sm font-body font-medium rounded-full px-7 py-3.5 transition-all"
          >
            {t("hero.cta_consult")}
          </button>
        </div>
      </div>

      {/* Floating remote — RIGHT side, symmetric inset (distance from bottom
          equals distance from side). */}
      <div
        data-testid="remote-wrapper"
        className="absolute z-20 right-8 md:right-12 bottom-8 md:bottom-12 w-24 md:w-32 lg:w-36 flex flex-col items-center"
      >
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 backdrop-blur border border-white/60 shadow-sm whitespace-nowrap">
          <span
            className={`h-1.5 w-1.5 rounded-full transition-colors ${
              isOpaque ? "bg-[#86868B]" : "bg-[#34C759]"
            }`}
          />
          <span className="text-[10px] font-body font-semibold tracking-[0.22em] uppercase text-[#1D1D1F]/80">
            {isOpaque ? t("hero.status_on") : t("hero.status_off")}
          </span>
        </div>

        <button
          data-testid="smart-glass-toggle-btn"
          aria-label="Toggle smart glass"
          onClick={() => setIsOpaque((v) => !v)}
          className="relative group transition-transform duration-300 hover:scale-[1.04] active:scale-[0.98] w-full"
        >
          <span className="absolute inset-3 rounded-full remote-pulse pointer-events-none" />
          <img
            src={MEDIA.remote}
            alt="Smart glass remote"
            className="relative w-full drop-shadow-[0_18px_40px_rgba(0,0,0,0.25)]"
            draggable="false"
          />
        </button>

        <span className="absolute -bottom-7 -right-6 md:right-auto md:left-1/2 md:-translate-x-1/2 text-[10px] md:text-[11px] font-body tracking-[0.22em] uppercase text-[#1D1D1F]/60 whitespace-nowrap">
          {t("hero.tap_to_switch")}
        </span>
      </div>

      {/* Scroll cue */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-[#1D1D1F]/50">
        <span className="text-[10px] font-body tracking-[0.3em] uppercase">
          {t("hero.scroll")}
        </span>
        <span className="block w-px h-10 bg-[#1D1D1F]/30" />
      </div>
    </section>
  );
}
